import type { Gravity, ProcessingOptions } from './types'
type ProcessingFunction = (params: ProcessingOptions) => string

const createOption = (name: string, value: string | number | boolean): string => `${name}:${value}/`

const processBasicOptions: ProcessingFunction = (params) => {
  const options: string[] = []

  if (params.resizingType) {
    options.push(createOption('resizing_type', params.resizingType))
  }
  if (params.width) {
    options.push(createOption('width', params.width))
  }
  if (params.height) {
    options.push(createOption('height', params.height))
  }
  if (params.minWidth) {
    options.push(createOption('min-width', params.minWidth))
  }
  if (params.minHeight) {
    options.push(createOption('min-height', params.minHeight))
  }
  if (params.dpr) {
    options.push(createOption('dpr', params.dpr))
  }
  if (params.enlarge) {
    options.push(createOption('enlarge', params.enlarge))
  }

  if (params.returnAttachment) {
    options.push(createOption('return_attachment', params.returnAttachment))
  }

  return options.join('')
}

const processDimensionOptions: ProcessingFunction = (params) => {
  const options: string[] = []

  if (params.resize) {
    const { resizingType, width, height, enlarge, extend } = params.resize
    options.push(`resize:${resizingType || 'fit'}:${width || 0}:${height || 0}:${enlarge || false}:${extend || false}/`)
  }

  if (params.size) {
    const { width, height, enlarge, extend } = params.size
    options.push(`size:${width || 0}:${height || 0}:${enlarge || false}:${extend || false}/`)
  }

  return options.join('')
}
const getGravity = (gravity?: Gravity) => {
  if (!gravity?.type) {
    return 'ce'
  } else if (typeof gravity.type === 'string') {
    return gravity.type
  } else {
    return `${gravity.type.x}:${gravity.type.y}`
  }
}
const processPositionOptions: ProcessingFunction = (params) => {
  const options: string[] = []

  if (params.extend) {
    const { extend } = params
    const extendGravityType = getGravity(extend.gravity)
    options.push(
      `extend:${extend.extend || false}:${extendGravityType}:${extend.gravity?.offset?.x || 0}:${extend.gravity?.offset?.y || 0}/`,
    )
  }

  if (params.gravity) {
    const { gravity } = params
    const gravityType = getGravity(gravity)
    options.push(`gravity:${gravityType}:${gravity.offset?.x || 0}:${gravity.offset?.y || 0}/`)
  }

  if (params.crop) {
    const { crop } = params
    const gravityType = getGravity(crop.gravity)
    options.push(
      `crop:${crop.width}:${crop.height}:${crop.width}:${crop.height}:${gravityType}:${crop.gravity?.offset?.x || 0}:${crop.gravity?.offset?.y || 0}/`,
    )
  }

  return options.join('')
}

const processEnhancementOptions: ProcessingFunction = (params) => {
  const options: string[] = []

  if (params.blur) {
    options.push(createOption('blur', params.blur))
  }
  if (params.sharpen) {
    options.push(createOption('sharpen', params.sharpen))
  }
  if (params.pixelate) {
    options.push(createOption('pixelate', params.pixelate))
  }
  if (params.quality) {
    options.push(createOption('quality', params.quality))
  }

  if (params.background) {
    const { background } = params
    if (typeof background === 'string') {
      options.push(createOption('background', background))
    } else {
      options.push(`background:${background.r || 0}:${background.g || 0}:${background.b || 0}/`)
    }
  }

  return options.join('')
}

const processMetadataOptions: ProcessingFunction = (params) => {
  const options: string[] = []

  if (params.stripMetadata) {
    options.push(createOption('strip_metadata', params.stripMetadata))
  }
  if (params.keepCopyright) {
    options.push(createOption('keep_copyright', params.keepCopyright))
  }
  if (params.stripColorProfile) {
    options.push(createOption('strip_color_profile', params.stripColorProfile))
  }
  if (params.maxBytes) {
    options.push(createOption('max_bytes', params.maxBytes))
  }

  return options.join('')
}

const processFormatOptions: ProcessingFunction = (params) => {
  const options: string[] = []

  if (params.format) {
    options.push(createOption('format', params.format))
  }
  if (params.formatQuality) {
    let formatQualityParam = 'format_quality:'
    Object.entries(params.formatQuality).forEach(([key, value]) => {
      formatQualityParam += `${key}:${value}:`
    })
    options.push(`${formatQualityParam.slice(0, -1)}/`)
  }

  return options.join('')
}

export const getImage = (params: ProcessingOptions) => {
  if (!params.id) {
    return undefined
  }

  const processingFunctions = [
    processBasicOptions,
    processDimensionOptions,
    processPositionOptions,
    processEnhancementOptions,
    processMetadataOptions,
    processFormatOptions,
  ]

  const imgproxy_processing_options = processingFunctions
    .map((fn) => fn(params))
    .join('')
    .slice(0, -1)

  return `/api/media/proxy/${params.id}${imgproxy_processing_options}`
}
